import {
  ProviderSettingsRequestError,
  requestProviderSettings,
} from '@/services/provider/settings-request';
import type {
  ProviderNotificationSettings,
  UpdateProviderNotificationSettingsInput,
} from '@/types/provider-notification-settings';

type ApiPayload = {
  data?: unknown;
  message?: string;
  errors?: Record<string, string[]>;
};

const readString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

const normalizeOptionalString = (value: unknown): string | null => {
  const trimmed = readString(value)?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : null;
};

const normalizeEmailList = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => readString(entry)?.trim())
    .filter((entry): entry is string => Boolean(entry && entry.length > 0));
};

function normalizeSettings(payload: ApiPayload | undefined): ProviderNotificationSettings {
  const record =
    (payload?.data as Record<string, unknown> | undefined) ??
    ((payload as Record<string, unknown> | undefined) ?? {});
  const email = (record.email as Record<string, unknown> | undefined) ?? {};
  const from = (email.from as Record<string, unknown> | undefined) ?? {};

  return {
    bccEmails: normalizeEmailList(email.bcc),
    fromAddress: normalizeOptionalString(from.address),
    fromName: normalizeOptionalString(from.name),
  };
}

export class ProviderNotificationSettingsRequestError extends ProviderSettingsRequestError {
  errors?: Record<string, string[]>;

  constructor(message: string, status?: number, payload?: unknown) {
    super(message, status, payload);
    if (payload && typeof payload === 'object' && 'errors' in (payload as Record<string, unknown>)) {
      this.errors = (payload as { errors?: Record<string, string[]> }).errors;
    }
  }
}

async function requestNotificationSettings(
  path: string,
  options: { method?: 'GET' | 'PUT'; body?: Record<string, unknown> | undefined } = {},
): Promise<ApiPayload> {
  try {
    return (await requestProviderSettings(path, options)) as ApiPayload;
  } catch (error) {
    if (error instanceof ProviderSettingsRequestError) {
      throw new ProviderNotificationSettingsRequestError(error.message, error.status, error.payload);
    }
    throw new ProviderNotificationSettingsRequestError(
      'Provider notification settings request failed',
      undefined,
      error,
    );
  }
}

class ProviderNotificationSettingsService {
  async getSettings(): Promise<ProviderNotificationSettings> {
    const response = await requestNotificationSettings('/api/v1/provider/settings/notifications');
    return normalizeSettings(response);
  }

  async updateSettings(
    input: UpdateProviderNotificationSettingsInput,
  ): Promise<ProviderNotificationSettings> {
    const response = await requestNotificationSettings('/api/v1/provider/settings/notifications', {
      method: 'PUT',
      body: {
        email: {
          bcc: input.bccEmails,
          from: {
            address: input.fromAddress,
            name: input.fromName,
          },
        },
      },
    });

    return normalizeSettings(response);
  }
}

export const providerNotificationSettingsService = new ProviderNotificationSettingsService();
